import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import Dropzone from 'components/base/Dropzone';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faFileLines,
  faClock,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { uploadPolicyDocument, fetchCSAPlusProject, CSAPlusUploadItem } from '../data-access/csa-plus.api';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { useDispatch } from 'react-redux';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/xml': ['.xml'],
  'text/xml': ['.xml'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv']
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleString();
};

const PoliciesFrameworksTab = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploads, setUploads] = useState<CSAPlusUploadItem[]>([]);
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const loadUploads = useCallback(async () => {
    console.log('[loadUploads] Starting');
    if (!projectId) return;
    setIsLoadingUploads(true);
    try {
      const project = await fetchCSAPlusProject(projectId);
      console.log('[loadUploads] Got project, uploads is:', project?.uploads);

      if (!project?.uploads) {
        setUploads([]);
        return;
      }

      // Always treat as potential object, convert if needed
      let uploadList: CSAPlusUploadItem[] = [];
      const raw = project.uploads as any;

      if (Array.isArray(raw)) {
        uploadList = raw.filter((item: any) => item && item.doc_type === 'policy');
      } else {
        // It's an object, convert values to array
        uploadList = Object.keys(raw)
          .map((key: string) => raw[key])
          .filter((item: any) => item && item.doc_type === 'policy');
      }

      console.log('[loadUploads] Final list:', uploadList);
      setUploads(uploadList);

      const allSettled = uploadList.every(u => ['extracted', 'indexed', 'failed'].includes(u.status));
      if (allSettled) stopPolling();
    } catch (err) {
      console.error('[loadUploads] Error:', err);
      setUploads([]);
    } finally {
      setIsLoadingUploads(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  // Poll every 30s while any upload is still processing
  useEffect(() => {
    const hasPending = uploads.some(u => !['extracted', 'indexed', 'failed'].includes(u.status));
    if (hasPending && pollRef.current === null) {
      pollRef.current = setInterval(() => {
        loadUploads();
      }, 30000);
    }
    return stopPolling;
  }, [uploads, loadUploads, stopPolling]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDrop = (acceptedFiles: File[], _fileRejections: unknown[], _event: unknown) => {
    setSelectedFile(acceptedFiles[0] ?? null);
  };

  const handleUpload = async () => {
    if (!projectId || !selectedFile) {
      dispatch(
        setToast({
          show: true,
          title: 'No file selected',
          message: 'Please select a document (PDF, DOC, or DOCX) to upload.'
        })
      );
      return;
    }

    setIsUploading(true);
    try {
      const res = (await uploadPolicyDocument({
        projectId,
        file: selectedFile,
        docType: 'policy',
        convertToVector: true
      })) as { replaced?: boolean; original_filename?: string } | null;
      if (res?.replaced) {
        dispatch(
          setToast({
            show: true,
            title: 'Policy updated',
            message: `"${res.original_filename}" has been replaced with the latest version. The previous version is no longer included in rule extraction.`
          })
        );
      } else {
        dispatch(
          setToast({
            show: true,
            title: 'Upload successful',
            message: 'Policy document has been uploaded successfully.'
          })
        );
      }
      setSelectedFile(null);
      setDropzoneKey(k => k + 1);
      await loadUploads();
    } catch (err: any) {
      console.error('Policy upload failed:', err);
      if (err?.response?.status === 409) {
        const { original_filename, uploaded_at } = err.response.data;
        const dateStr = uploaded_at ? new Date(uploaded_at).toLocaleString() : 'a previous session';
        dispatch(
          setToast({
            show: true,
            title: 'Duplicate file',
            message: `Identical content already uploaded as "${original_filename}" on ${dateStr}.`
          })
        );
      } else {
        dispatch(
          setToast({
            show: true,
            title: 'Upload failed',
            message: err?.response?.data?.message || err?.message || 'Failed to upload document.'
          })
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-4 p-lg-5">
      <h5 className="fw-bold mb-1 text-body">Policies & Frameworks</h5>
      <p className="text-body-secondary mb-4 mb-lg-5 small">
        Upload policy documents (XML, XLS, XLSX, CSV, PDF, DOC, DOCX) for governance frameworks.
      </p>

      <Row className="g-4 g-lg-5">
        {/* Left column - Upload */}
        <Col xs={12} lg={6}>
          <div className="rounded-3 shadow-sm border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-4 text-body">Upload policy document</h6>
            <div className="mb-4">
              <Dropzone
                key={dropzoneKey}
                accept={ACCEPTED_TYPES}
                onDrop={handleDrop}
                reactDropZoneProps={{ multiple: false, maxFiles: 1 }}
              >
                <div className="text-center py-5">
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    className="text-body-tertiary mb-3"
                    size="2x"
                    style={{ opacity: 0.7 }}
                  />
                  <p className="text-body-tertiary mb-1 fw-semibold">Drag and drop your file here</p>
                  <p className="text-muted small mb-0">
                    or click to browse · Accepts .xml, .xls, .xlsx, .csv, .doc, .docx, .pdf
                  </p>
                </div>
              </Dropzone>
            </div>
            <Button
              variant="primary"
              className="csa-upload-btn d-flex align-items-center justify-content-center gap-2 w-100 py-2"
              style={{ backgroundColor: '#252a36', borderColor: '#252a36' }}
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCloudArrowUp} />
                  Upload
                </>
              )}
            </Button>
          </div>
        </Col>

        {/* Right column - Recent uploads */}
        <Col xs={12} lg={6}>
          <div className="rounded-3 shadow-sm border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-4 text-body">Recent uploads</h6>
            {isLoadingUploads ? (
              <div className="text-center py-6">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted small mt-2 mb-0">Loading uploads...</p>
              </div>
            ) : uploads.length === 0 ? (
              <div className="text-center py-6">
                <FontAwesomeIcon
                  icon={faFileLines}
                  className="text-body-tertiary mb-2"
                  size="2x"
                  style={{ opacity: 0.5 }}
                />
                <p className="text-muted small mb-0">No uploads yet. Upload a document to get started.</p>
              </div>
            ) : (
              <>
                <div className="d-flex flex-column gap-2" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                  {uploads.map(upload => (
                    <div
                      key={upload.upload_id}
                      className="rounded-2 p-3 border bg-light d-flex align-items-start gap-3"
                    >
                      <div className="flex-shrink-0 mt-1">
                        {['extracted', 'indexed'].includes(upload.status) ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-success"
                            size="sm"
                            title="Ready for analysis"
                          />
                        ) : upload.status === 'failed' ? (
                          <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" size="sm" />
                        ) : (
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="text-warning"
                            size="sm"
                            title="Processing rules..."
                          />
                        )}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <p className="fw-semibold mb-1 text-truncate small" title={upload.original_filename}>
                          {upload.original_filename}
                        </p>
                        <div className="d-flex align-items-center gap-2 text-muted small">
                          <FontAwesomeIcon icon={faClock} size="xs" />
                          <span>{formatDate(upload.uploaded_at)}</span>
                        </div>
                        <div className="d-flex align-items-center gap-1 mt-1">
                          <span className="badge bg-light text-dark border small">{upload.doc_type}</span>
                          {(upload.status === 'extracted' ||
                            (upload.rules_extracted !== undefined && upload.rules_extracted > 0)) && (
                            <span className="badge bg-success-subtle text-success border small">Extracted</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const totalRules = Math.max(0, ...uploads.map(u => u.rules_extracted ?? 0));
                  return totalRules > 0 ? (
                    <p className="text-muted small mb-0 mt-2">
                      <span className="badge bg-info-subtle text-info border small me-1">{totalRules} Rules</span>
                      extracted in total across all policy files
                    </p>
                  ) : null;
                })()}
              </>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PoliciesFrameworksTab;
